// Forecast y Proyecciones — la salida real del motor (ticket 27).
//
// Esta pantalla mostraba una curva inventada rotulada "SARIMA + XGBoost · MAPE 9.4 %", generada
// por shared/forecast.ts con una demanda base de 430 unidades hardcodeada. Ese archivo se borró.
//
// Lo que hay ahora sale de la venta real, y hay dos cosas que la pantalla dice de frente
// porque cambian cómo se lee todo lo demás:
//   1. la proyección global es PLANA — con este historial no hay estacionalidad que modelar;
//   2. si el tiempo de producción no está cargado, las fechas de compra salen de un supuesto.
//
// El error que el motor mide en backtest se sigue calculando y guardando, pero NO se muestra
// (decisión de Charly, 2026-09-01): un porcentaje de error en pantalla se lee como una
// garantía sobre lo que va a pasar, y con este historial no lo es.
import { useState } from 'react';
import { AlertTriangle, Download, Search, X } from 'lucide-react';
import { Badge, Button, DataState, EmptyState, FilterTabs, Panel, PanelHeader, Table, Td, Thead, Tr } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { exportarForecast } from '@/lib/exports';
import { FichaProducto } from '@/components/domain/FichaProducto';
import { DemandForecastChart } from '@/components/charts/DemandForecastChart';
import { useForecast } from '@/lib/hooks';
import { formatCompactCop, formatNumber, formatShortDate } from '@shared/format';
import { cn } from '@/lib/cn';
import type { Tone } from '@/lib/status';
import type { ForecastSku, MetodoForecastDto } from '@shared/types';

const HORIZONTES = [
  { key: '30', label: '30d' },
  { key: '60', label: '60d' },
  { key: '90', label: '90d' },
];

/**
 * ⚠️ Un método que la base tenga y este mapa NO conozca NO puede romper la pantalla.
 *
 * Pasó en producción y la dejó en blanco: el estimador se cambió el 2026-08-27 y desde
 * entonces el mapa sólo tiene `media_365` y `ninguno`, pero `forecast_sku.metodo` guarda lo
 * que escribió la ÚLTIMA corrida del motor — que en producción era del 26/08 y traía todavía
 * `croston_sba` (193 filas), `ses` (75) y `holt` (10). `METODO[item.metodo]` daba `undefined`
 * y leer `.tone` tiraba la pantalla entera.
 *
 * La lección es más general que este caso: **un mapa indexado por un dato de la base necesita
 * salida por defecto**, porque la base puede tener valores viejos que el código ya no espera y
 * eso no se ve en el typecheck — `Record<MetodoForecastDto, …>` promete que el índice es uno
 * de los dos valores, y en tiempo de ejecución llega un string cualquiera.
 */
function metodoDe(valor: string): { label: string; tone: Tone; ayuda: string } {
  const conocido = METODO[valor as MetodoForecastDto];
  if (conocido) return conocido;
  // Se dice lo que es —un cálculo anterior— en vez de disimularlo: ese producto todavía
  // arrastra el método viejo y su número se rehace en la próxima corrida del motor.
  return {
    label: 'cálculo anterior',
    tone: 'neutral',
    ayuda: `Calculado con un método que ya no se usa (${valor}). Se actualiza en la próxima corrida del motor, apretando «Actualizar datos» en el Dashboard.`,
  };
}

type Orden = 'urgencia' | 'comprar' | 'demanda' | 'cobertura' | 'nombre';

/** Mismo tamaño de página que el semáforo y las recomendaciones, por el mismo motivo: una
 *  tabla larga con explicaciones adentro se siente pesada mucho antes de los 667. */
const POR_PAGINA = 50;

const METODO: Record<MetodoForecastDto, { label: string; tone: Tone; ayuda: string }> = {
  // Desde el 2026-08-27 el método es uno solo para todos: la media del último año. Lo que
  // distingue a un producto de otro es su FORMA, que tiene su propia columna.
  media_365: {
    label: 'Media 365 d', tone: 'info',
    ayuda: 'Promedio de venta del último año. Se eligió midiendo: sobre las series reales del ERP erraba la mitad que el suavizamiento anterior.',
  },
  ninguno: { label: 'sin serie', tone: 'neutral', ayuda: 'No hay historial suficiente para proyectar.' },
};

// Cada columna técnica lleva su término del glosario: son nombres de manual de inventarios
// ("stock de seguridad", "punto de reorden") y quien los mira es alguien de compras. Hasta ahora la única explicación era la columna "razón", que explica ESE producto
// y no el concepto.
const COLUMNAS = [
  { key: 'producto', label: 'Producto' },
  { key: 'metodo', label: 'Método', wrap: true, ayuda: 'metodo' as const },
  { key: 'demanda', label: 'Demanda/Día', align: 'right' as const, wrap: true, ayuda: 'demandaDia' as const },
  { key: 'stock', label: 'Stock', align: 'right' as const, ayuda: 'stock' as const },
  { key: 'ss', label: 'Stock de Seguridad', align: 'right' as const, wrap: true, ayuda: 'stockSeguridad' as const },
  { key: 'rop', label: 'Punto de Reorden', align: 'right' as const, wrap: true, ayuda: 'puntoReorden' as const },
  { key: 'cuando', label: 'Cuándo Pedir', align: 'right' as const, wrap: true, ayuda: 'cuandoPedir' as const },
];

export default function ForecastPage() {
  const forecast = useForecast();
  const [horizonte, setHorizonte] = useState('60');
  const [filtro, setFiltro] = useState('todos');
  const [fichaSku, setFichaSku] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<Orden>('urgencia');
  const [pagina, setPagina] = useState(0);

  return (
    <DataState query={forecast}>
      {(f) => {
        if (!f.resumen.productos) {
          return (
            <div className="space-y-6">
              <PageHeader title="Forecast y Proyecciones" subtitle="Demanda proyectada por SKU" />
              <EmptyState message="Todavía no se calculó el forecast. Entrá al Dashboard y apretá «Actualizar datos»: trae la venta del B2B y corre el motor." />
            </div>
          );
        }
        // ⚠️ Antes esto se cortaba en 100 y ahí moría: con 667 productos, los 567 restantes
        // no había forma de verlos — ni buscándolos, porque tampoco había buscador. Un
        // listado que muestra una parte y no da manera de llegar al resto es un listado roto.
        const filtradas = ordenar(filtrar(f.items, filtro, busqueda), orden);
        const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
        const pagActual = Math.min(pagina, paginas - 1);
        const visibles = filtradas.slice(pagActual * POR_PAGINA, (pagActual + 1) * POR_PAGINA);
        /** Buscar, filtrar u ordenar vuelve a la página 1: si no, se ve una tabla vacía. */
        const desdeElPrincipio = (fn: () => void) => {
          fn();
          setPagina(0);
        };
        return (
          <div className="space-y-6">
            <PageHeader
              title="Forecast y Proyecciones"
              subtitle={`${f.resumen.conProyeccion} de ${f.resumen.productos} productos tienen historial suficiente para proyectar${
                f.resumen.calculadoAt ? ` · calculado el ${formatShortDate(f.resumen.calculadoAt.slice(0, 10))}` : ''
              }`}
            />

            {/* Sin el tiempo de producción real, "cuándo pedir" sale de un supuesto de 30 días.
                Se avisa arriba de todo: es lo que más cambia la fecha de una orden de compra. */}
            {f.resumen.sinLeadTimeReal > 0 && (
              <div className="surface flex items-start gap-3 border-l-2 border-l-warning p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" />
                <p className="text-xs leading-relaxed text-ink-muted">
                  <strong className="text-ink-strong">
                    {formatNumber(f.resumen.sinLeadTimeReal)}{' '}
                    {f.resumen.sinLeadTimeReal === 1
                      ? 'producto no tiene cargado su tiempo de producción'
                      : 'productos no tienen cargado su tiempo de producción'}
                  </strong>{' '}
                  y se {f.resumen.sinLeadTimeReal === 1 ? 'está' : 'están'} calculando con 30 días por defecto. Las cantidades a comprar son
                  correctas, pero <em>la fecha</em> en que hay que poner la orden depende de ese
                  dato: cargalo en Configuración → Configuración por Producto.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Tile label="Demanda proyectada" value={formatNumber(f.resumen.demandaDiariaTotal)} sub="unidades por día, todo el catálogo" />
              <Tile
                label="Con proyección"
                value={formatNumber(f.resumen.conProyeccion)}
                sub={`de ${formatNumber(f.resumen.productos)} productos tienen serie suficiente`}
              />
              <Tile label="Hay que comprar" value={formatNumber(f.resumen.aComprar)} sub="productos en punto de reorden" tone={f.resumen.aComprar > 0 ? 'warning' : 'success'} />
              {/* La guarda era `capital > 0` y eso NO alcanza: con 59 de 61 productos en costo
                  0, el total daba $15.950 COP — positivo, y absurdo. Lo que decide es si
                  falta algún costo, no si la suma dio más que cero. */}
              <Tile
                label="Capital estimado"
                value={f.resumen.sinCosto > 0 ? 'sin costos' : formatCompactCop(f.resumen.capital)}
                sub={
                  f.resumen.sinCosto > 0
                    ? `${f.resumen.sinCosto} de ${f.resumen.aComprar} sin valor unitario cargado`
                    : 'para cubrir el objetivo'
                }
              />
            </div>

            <Panel>
              <PanelHeader
                title="Proyección de Demanda Total"
                subtitle={`Banda al ${f.meta.confidence}% de nivel de servicio · ${f.meta.model}`}
                right={<FilterTabs items={HORIZONTES} value={horizonte} onChange={setHorizonte} />}
              />
              <div className="p-5">
                <DemandForecastChart points={f.points} meta={f.meta} horizon={Number(horizonte)} />
                <p className="mt-4 text-2xs leading-relaxed text-ink-faint">
                  La línea proyectada es plana a propósito: con el historial disponible no se puede
                  modelar estacionalidad — para eso hay que ver el mismo período repetido al menos
                  dos veces. Lo que sí aporta el motor es el nivel de demanda de cada producto y su
                  variabilidad, que es de donde salen el stock de seguridad y el punto de reorden.
                </p>
              </div>
            </Panel>

            <Panel>
              <PanelHeader
                title="Detalle por Producto"
                subtitle="Cada fila explica de dónde sale su número"
                right={
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="subtle"
                      icon={<Download className="h-3.5 w-3.5" />}
                      onClick={() => exportarForecast(filtradas)}
                      title="Baja lo que estás viendo, con el filtro aplicado"
                    >
                      Exportar
                    </Button>
                    <FilterTabs
                    items={[
                      { key: 'todos', label: 'Todos', count: f.items.length },
                      { key: 'comprar', label: 'A COMPRAR', count: f.resumen.aComprar, tone: 'warning' },
                      { key: 'proyectados', label: 'CON PROYECCIÓN', count: f.resumen.conProyeccion, tone: 'success' },
                      { key: 'sin_datos', label: 'SIN DATOS', count: f.resumen.sinDatos, tone: 'neutral' },
                    ]}
                      value={filtro}
                      onChange={(v) => desdeElPrincipio(() => setFiltro(v))}
                    />
                  </div>
                }
              />
              <div className="flex flex-col gap-3 border-b border-hairline px-4 py-3 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                  <input
                    value={busqueda}
                    onChange={(e) => desdeElPrincipio(() => setBusqueda(e.target.value))}
                    placeholder="Buscar por SKU o producto…"
                    className="h-9 w-full rounded-lg border border-hairline bg-card-2 pl-9 pr-8 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-ring/40"
                  />
                  {busqueda && (
                    <button
                      onClick={() => desdeElPrincipio(() => setBusqueda(''))}
                      aria-label="Limpiar la búsqueda"
                      className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-ink-faint hover:bg-hover hover:text-ink-strong"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <label className="flex items-center gap-2">
                  <span className="text-2xs uppercase tracking-wide text-ink-faint">Ordenar por</span>
                  <select
                    value={orden}
                    onChange={(e) => desdeElPrincipio(() => setOrden(e.target.value as Orden))}
                    className="h-9 rounded-lg border border-hairline bg-card-2 px-2.5 text-xs text-ink focus:border-primary focus:outline-none"
                  >
                    <option value="urgencia">Más urgentes primero</option>
                    <option value="comprar">Mayor cantidad a comprar</option>
                    <option value="demanda">Mayor demanda por día</option>
                    <option value="cobertura">Menos días de cobertura</option>
                    <option value="nombre">Nombre</option>
                  </select>
                </label>
                <p className="shrink-0 text-2xs text-ink-faint">
                  {filtradas.length === f.items.length
                    ? `${formatNumber(f.items.length)} productos`
                    : `${formatNumber(filtradas.length)} de ${formatNumber(f.items.length)}`}
                </p>
              </div>

              <Table>
                <Thead columns={COLUMNAS} />
                <tbody>
                  {visibles.map((i) => (
                    <FilaSku key={i.sku} item={i} onAbrir={setFichaSku} />
                  ))}
                </tbody>
              </Table>

              {filtradas.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-ink-muted">
                  Ningún producto coincide con lo que buscaste.
                </p>
              ) : (
                paginas > 1 && (
                  <div className="flex items-center justify-between gap-3 border-t border-hairline px-4 py-3">
                    <p className="text-xs text-ink-muted">
                      {pagActual * POR_PAGINA + 1}–{Math.min((pagActual + 1) * POR_PAGINA, filtradas.length)} de{' '}
                      {formatNumber(filtradas.length)}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" disabled={pagActual === 0} onClick={() => setPagina(pagActual - 1)}>
                        Anterior
                      </Button>
                      <span className="text-xs text-ink-muted">
                        {pagActual + 1} / {paginas}
                      </span>
                      <Button
                        variant="outline"
                        disabled={pagActual >= paginas - 1}
                        onClick={() => setPagina(pagActual + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )
              )}
            </Panel>

            <FichaProducto sku={fichaSku} onCerrar={() => setFichaSku(null)} />
          </div>
        );
      }}
    </DataState>
  );
}

function FilaSku({ item, onAbrir }: { item: ForecastSku; onAbrir: (sku: string) => void }) {
  const m = metodoDe(item.metodo);
  const urgente = item.cantidadSugerida > 0;
  return (
    <Tr className={cn(urgente && 'border-l-2 border-l-warning')}>
      <Td>
        <button
          onClick={() => onAbrir(item.sku)}
          className="block text-left"
          title="Ver la ficha del producto"
        >
          <p className="font-medium text-ink-strong hover:text-primary-text">{item.name}</p>
          <p className="font-mono text-2xs text-ink-faint">{item.sku}</p>
        </button>
        {/* La explicación es el producto, no un extra: una orden que nadie puede justificar
            no se aprueba. */}
        <p className="mt-1 max-w-xl text-2xs leading-relaxed text-ink-muted">{item.razon}</p>
      </Td>
      <Td>
        <Badge tone={m.tone} className="whitespace-nowrap" >{m.label}</Badge>
        <p className="mt-1 text-2xs text-ink-faint">
          {item.diasConVenta}/{item.diasObservados} días con venta
        </p>
      </Td>
      <Td align="right">
        <span className="font-semibold text-ink-strong">{item.demandaDiaria}</span>
        <span className="block text-2xs text-ink-faint">± {item.sigmaDiaria}</span>
      </Td>
      <Td align="right"><span className="text-ink">{formatNumber(item.currentStock)}</span></Td>
      <Td align="right"><span className="text-ink">{formatNumber(item.stockSeguridad)}</span></Td>
      <Td align="right">
        <span className="text-ink">{formatNumber(item.puntoReorden)}</span>
        <span className="block text-2xs text-ink-faint">
          {item.leadTimeSource === 'cargado' ? `${item.leadTimeDays}d de producción` : `${item.leadTimeDays}d por defecto`}
        </span>
      </Td>
      <Td align="right">
        {item.diasHastaReorden == null ? (
          <span className="text-2xs text-ink-faint">—</span>
        ) : item.diasHastaReorden <= 0 ? (
          <>
            <span className="font-semibold text-danger-text">ahora</span>
            <span className="block text-2xs text-ink-faint">comprar {formatNumber(item.cantidadSugerida)}</span>
          </>
        ) : (
          <>
            <span className="font-semibold text-ink-strong">en {item.diasHastaReorden}d</span>
            <span className="block text-2xs text-ink-faint">{item.fechaLimite ? formatShortDate(item.fechaLimite) : ''}</span>
          </>
        )}
      </Td>
    </Tr>
  );
}

function Tile({ label, value, sub, tone = 'neutral' }: { label: string; value: string; sub: string; tone?: Tone }) {
  const color =
    tone === 'success' ? 'text-success-text' : tone === 'warning' ? 'text-warning-text' : tone === 'danger' ? 'text-danger-text' : 'text-ink-strong';
  return (
    <div className="surface p-4">
      <p className="text-2xs uppercase tracking-wide text-ink-faint">{label}</p>
      <p className={cn('mt-1 text-2xl font-bold', color)}>{value}</p>
      <p className="mt-0.5 text-2xs text-ink-faint">{sub}</p>
    </div>
  );
}

function filtrar(items: ForecastSku[], filtro: string, busqueda: string): ForecastSku[] {
  const q = busqueda.trim().toLowerCase();
  return items.filter((i) => {
    if (filtro === 'comprar' && i.cantidadSugerida <= 0) return false;
    if (filtro === 'proyectados' && i.forma === 'sin_serie') return false;
    if (filtro === 'sin_datos' && i.forma !== 'sin_serie') return false;
    if (!q) return true;
    return i.sku.toLowerCase().includes(q) || i.name.toLowerCase().includes(q);
  });
}

/**
 * El orden por defecto es «más urgentes primero», que es el que ya traía la lista del server:
 * primero lo que hay que comprar, y dentro de eso lo que menos aguanta.
 */
function ordenar(items: ForecastSku[], orden: Orden): ForecastSku[] {
  // `diasHastaReorden` es null cuando el producto no tiene serie con la que proyectar. Va
  // ÚLTIMO en los dos órdenes que lo usan: tratarlo como 0 lo pondría entre los más urgentes,
  // que es justo lo contrario — de ese producto no sabemos nada.
  const cuando = (i: ForecastSku) => i.diasHastaReorden ?? Number.POSITIVE_INFINITY;
  const cmp: Record<Orden, (a: ForecastSku, b: ForecastSku) => number> = {
    urgencia: (a, b) =>
      Number(b.cantidadSugerida > 0) - Number(a.cantidadSugerida > 0) || cuando(a) - cuando(b),
    comprar: (a, b) => b.cantidadSugerida - a.cantidadSugerida,
    demanda: (a, b) => b.demandaDiaria - a.demandaDiaria,
    cobertura: (a, b) => cuando(a) - cuando(b),
    nombre: (a, b) => a.name.localeCompare(b.name),
  };
  return [...items].sort(cmp[orden]);
}
